//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { RouteAnchor } from '@components/RouteAnchor';
import Badge, { BadgeType } from '@components/atoms/badge/Badge';
import { BodySecondaryText, BodyText } from '@components/atoms/text/Text';
import { TrackingId } from '@models/TrackingId';
import { isDefined } from '@utils/filterPredicates';
import { withTracking } from '@utils/withTracking';

import { hasItemAccessByStateName } from 'app/initialization/routing/hasItemAccessByStateName';
import { DashboardStateNames } from 'app/top-level-states';

const shouldShowNewBadge = (newTagExpiration?: string | number | Date) => {
  const today = new Date();
  if (isDefined(newTagExpiration)) {
    const newDateExpiration = new Date(newTagExpiration);
    return today < newDateExpiration;
  }
  return false;
};

interface Props {
  isBeta?: boolean;
  newTagExpiration?: string | number | Date;
  primary: ReactNode;
  secondary?: ReactNode;
  state: DashboardStateNames;
  trackingId: TrackingId;
}

export const NavDropdownItem = ({
  isBeta,
  newTagExpiration,
  primary,
  secondary,
  state,
  trackingId,
}: Props) => {
  const isPathAllowed = hasItemAccessByStateName(state);
  const isNew = shouldShowNewBadge(newTagExpiration);

  if (!isPathAllowed) {
    return null;
  }

  return (
    <StyledRouteAnchor
      trackingId={trackingId}
      state={state}
      stateParams={{ viaTopNav: true }}
    >
      <HeaderWrapper>
        <BodyText>{primary}</BodyText>
        {isBeta && (
          <StyledBadge text='Beta' type={BadgeType.New} size='small' />
        )}
        {isNew && <StyledBadge text='New' type={BadgeType.New} size='small' />}
      </HeaderWrapper>
      <BodySecondaryText>{secondary}</BodySecondaryText>
    </StyledRouteAnchor>
  );
};

const StyledBadge = styled(Badge)(
  ({ theme }) => `
    margin-left: ${theme.spacings[2]};
  `,
);

const HeaderWrapper = styled.div`
  display: flex;
`;

const StyledRouteAnchor = withTracking(styled(RouteAnchor)`
  display: block;
`);
